import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from 'react-i18next';
import { FormInput, FormButton } from "./FormComponents";
import { API_ENDPOINTS, authenticatedFetch } from "../config/api";
import { isAuthenticatedAndValid, storeAuthData, LoginResponse, getAppType } from "../utils/auth";
import { setUserProfile, updateStreakSpark } from "../store/slices/userSlice";
import { toUUID } from "../utils/uuidUtils";
import { useAppDispatch } from "../store/hooks";
import { trackPageVisit, trackRegisterEvent } from "../utils/analytics";

interface RegisterFormData {
  username: string;
  email: string;
  password: string;
  name: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<RegisterFormData>({
    username: "",
    email: "",
    password: "",
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticatedAndValid()) {
      navigate("/app");
    }
  }, [navigate]);

  useEffect(() => {
    
    trackPageVisit('register');
  }, []);

  const handleInputChange =
    (field: keyof RegisterFormData) => (value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Determine app type for session validity
      const appType = getAppType();

      // TODO: Replace with actual API call to /auth/register
      const response = await authenticatedFetch(API_ENDPOINTS.AUTH.REGISTER, {
        method: "POST",
        body: JSON.stringify({ ...formData, app: appType }),
      });

      if (!response.ok) {
        throw new Error("Registration failed");
      }

      const data: LoginResponse = await response.json();

      // If API returns auth data (token and user), store it
      if (data.accessToken && data.user) {
        storeAuthData(data);

        // Update Redux store with user profile
        dispatch(
          setUserProfile({
            id: toUUID(data.user.id),
            username: data.user.username,
            email: data.user.email,
            name: data.user.name,
            password: "", // Don't store password in Redux
            createdAt: new Date().toISOString(),
            sparkPoints: data.user.sparkPoints || 0, // Default to 0 if not provided
            preferences: {
              dailyReminderNotificationTime: "20:00", // Default value
            },
            encryptionKey: "", // Default to empty string
            isEmailVerified: data.user.isEmailVerified || false, // Add email verification status
          })
        );

        // Update streak spark data with total spark points
        dispatch(
          updateStreakSpark({
            totalSparkPoints: data.user.sparkPoints || 0,
          })
        );

        // Determine redirect path
        const returnUrl = searchParams.get('returnUrl');
        let redirectPath = '/app';
        if (returnUrl) {
          try {
            const decodedUrl = decodeURIComponent(returnUrl);
            // Basic validation: ensure it's an internal path
            if (decodedUrl.startsWith('/') && !decodedUrl.includes('://') && decodedUrl !== '/login' && decodedUrl !== '/register') {
              redirectPath = decodedUrl;
            }
          } catch (e) {
            // Invalid URL, use default
          }
        }

        // Navigate to the determined path
        navigate(redirectPath);
      } else {
        // If no auth data, navigate to login
        navigate("/login");
      }

      // Track successful registration event
      trackRegisterEvent(formData.username);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MetaHead
        title={t('register.title')}
        description={t('register.description') as string}
        keywords={t('register.keywords') as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md animate-fade-in">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-soft mb-6">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent mb-2">
            {t('register.joinTitle')}
          </h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t('register.subtitle')}
          </p>
        </div>

        {/* Registration Form */}
        <div className="glass-card p-8 rounded-2xl shadow-large">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-5">
              <FormInput
                id="name"
                label={t('register.fullName')}
                type="text"
                value={formData.name}
                onChange={handleInputChange("name")}
                placeholder={t('register.fullNamePlaceholder') as string}
                required
              />

              <FormInput
                id="username"
                label={t('register.username')}
                type="text"
                value={formData.username}
                onChange={handleInputChange("username")}
                placeholder={t('register.usernamePlaceholder') as string}
                required
              />

              <FormInput
                id="email"
                label={t('register.email')}
                type="email"
                value={formData.email}
                onChange={handleInputChange("email")}
                placeholder={t('register.emailPlaceholder') as string}
                required
              />

              <FormInput
                id="password"
                label={t('register.password')}
                type="password"
                value={formData.password}
                onChange={handleInputChange("password")}
                placeholder={t('register.passwordPlaceholder') as string}
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 text-red-500 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                    {error}
                  </p>
                </div>
              </div>
            )}

            <div className="w-full">
              <FormButton
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                disabled={loading}
              >
                {loading ? t('register.creatingAccount') : t('register.createAccount')}
              </FormButton>
            </div>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {t('register.alreadyHaveAccount')}{" "}
              <Link
                to="/login"
                className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
              >
                {t('register.signInHere')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {t('register.byCreating')}{" "}
            <Link
              to="/terms"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              {t('register.termsOfService')}
            </Link>
          </p>
        </div>

        {/* Home Link */}
        <div className="text-center mt-4">
          <Link
            to="/"
            className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {t('register.backToHome')}
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default Register;
